import { useState } from "react";
import { DownloadSimple, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const SECTIONS = [/positioning/, /profile/, /60[\s-]*second/, /content engine/, /dream client/, /outreach/, /weekly hour/, /still employed/, /first (?:five|5) moves/];

// Mirrors backend blueprint_pdf.is_full_blueprint: all nine sections present as headings.
export function isFullBlueprint(text) {
  const heads = [...(text || "").matchAll(/^\s*(?:#{1,4}\s+|\*\*)(.+)$/gm)].map((m) => m[1].toLowerCase()).join("\n");
  return SECTIONS.every((re) => re.test(heads));
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    a.remove();
  }, 8000);
}

// Book Builder: the ASSEMBLE reply (front + back matter, not a COMPILE part) — mirrors backend book_pdf.is_assembled_book.
export function isAssembledBook(text) {
  const low = (text || "").toLowerCase();
  return low.includes("about the author") && low.includes("key terms") && (low.includes("preface") || low.includes("copyright")) && !/\bpart\s+\d+\s+of\s+\d+\b/.test(low);
}

// Credibility Code: the Stage 5 reply holding the finished document — mirrors backend credibility_pdf.extract_document.
export function isCredibilityCode(text) {
  const up = (text || "").toUpperCase();
  return up.includes("CREDIBILITY CODE") && ["THE EVIDENCE", "PROOF", "WORKING TOGETHER"].every((s) => up.includes(s));
}

// Document Builder: a reply whose ```text card is a finished document — mirrors backend document_pdf.extract_document.
export function isDocumentReply(text) {
  return [...(text || "").matchAll(/```(?:text|txt|plain)\s*\n([\s\S]*?)```/g)].some((m) => m[1].trim().length >= 300);
}

export async function downloadPdf(url, fallbackName = "Download.pdf") {
  const resp = await api.get(url, { responseType: "blob", timeout: 60000 });
  const cd = resp.headers["content-disposition"] || "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  const plain = /filename="([^"]+)"/i.exec(cd);
  saveBlob(resp.data, star ? decodeURIComponent(star[1]) : plain ? plain[1] : fallbackName);
}

export const downloadBlueprintPdf = (messageId) => downloadPdf(`/messages/${messageId}/blueprint.pdf`, "LinkedIn-Blueprint.pdf");

export default function BlueprintDownloadButton({ messageId, url, label = "Download my Blueprint (PDF)", className = "pill-cta pill-cta-outline", testId = "blueprint-download-button", children }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await (url ? downloadPdf(url) : downloadBlueprintPdf(messageId));
    } catch {
      toast.error("Couldn't prepare your PDF.", { action: { label: "Retry", onClick: run } });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={run} disabled={busy} className={className} data-testid={testId} aria-busy={busy}>
      {busy ? <CircleNotch size={14} weight="bold" className="spin" /> : <DownloadSimple size={14} weight="bold" />}
      {busy ? "Preparing your PDF…" : label}
      {children}
    </button>
  );
}
